import { getHsp, getHspIbge, getIndices } from './api';
import { classificarAdequacaoKit } from '../utils/adequacaoKit';
import { resolverPR } from '../utils/performanceRatio';

// ─── Constantes ────────────────────────────────────────────────────────────
// IDs fixos de orientação de telhado (mesmos usados no restante do sistema)
const ID_ORIENTACAO_LESTE_OESTE = '6508a00a326edddd6ff36617';
const ID_ORIENTACAO_SUL_SUDOESTE_SUDESTE = '6508a00a326edddd6ff36618';

/**
 * Dimensionamento On-Grid — extraído de EditProposta.js (era a função
 * interna `identificaKit`). Comportamento preservado 1:1; só os 3
 * `console.log` de depuração (idOrientacao, fatorOrientacao, HSP) foram
 * removidos — nenhuma lógica de cálculo mudou.
 *
 * Diferente de DimensionamentoBombeamento.js / DimensionamentoOffGrid.js,
 * este dimensionamento NÃO é uma etapa manual do wizard (sem botão
 * "Calcular"): roda sozinho, disparado pelo useEffect em EditProposta.js
 * sempre que fornecedor, módulo, orientação, consumo abatido ou topologia
 * mudam. Por isso a função aplica o resultado diretamente via os setters
 * recebidos, em vez de devolver um objeto para o componente aplicar (como
 * fazem os outros dois dimensionamentos).
 *
 * Calcula a Potência CC necessária a partir do consumo abatido, HSP da
 * cidade, Performance Ratio e fator de orientação, e seleciona
 * automaticamente o menor kit (fornecedor + módulo) que atenda essa
 * potência. Quando existem vários kits nessa mesma potência, expõe os
 * modelos de inversor disponíveis para que o usuário escolha a solução
 * comercial (string, microinversor, híbrido etc.) sem alterar a potência
 * fotovoltaica calculada.
 *
 * @param {object}   params.proposta
 * @param {function} params.setProposta
 * @param {function} params.setErro
 * @param {function} params.setInversoresDisponiveis
 * @param {function} params.setInversorSelecionado
 * @param {Array}    params.kits                 - kits On-Grid já carregados em EditProposta.js
 * @param {object}   params.inversorSelecionado - inversor escolhido entre os kits da potência calculada
 * @param {'%'|'R$'} params.tipoDesconto
 * @param {'%'|'R$'} params.tipoAcrescimo
 * @param {boolean}  params.termoAcrescimoAceito
 */
export async function identificarKitOnGrid({
  proposta,
  setProposta,
  setErro,
  setInversoresDisponiveis,
  setInversorSelecionado,
  kits,
  inversorSelecionado,
  tipoDesconto,
  tipoAcrescimo,
  termoAcrescimoAceito,
  setAdequacaoKit,
}) {
  const ufSigla = proposta.localinstalacao?.estado?.sigla;
  const nomeCidade = proposta.localinstalacao?.cidade?.nome;
  const ibgeCidade = proposta.localinstalacao?.cidade?.id; // código IBGE do IBGE API
  const moduloId = proposta.modulo?._id;
  const fornecedorId = proposta.fornecedor?._id;

  // ── Fator de Orientação ──────────────────────────────────────────
  let fatorOrientacao = 1.0;
  const idOrientacao = proposta.orientacaotelhado?._id?.toString();
  if (idOrientacao === ID_ORIENTACAO_LESTE_OESTE) {
    fatorOrientacao = 0.85; // Leste/Oeste
  } else if (idOrientacao === ID_ORIENTACAO_SUL_SUDOESTE_SUDESTE && !proposta.rebatimento) {
    fatorOrientacao = 0.70; // Sul/Sudoeste/Sudeste sem rebatimento
  }

  // ── Buscar HSP da cidade ─────────────────────────────────────────
  let hspData = null;
  try {
    // Tenta por IBGE primeiro — converte para Number pois API IBGE retorna string
    if (ibgeCidade) {
      hspData = await getHspIbge(Number(ibgeCidade));
    }
    // Fallback por nome+UF
    if (!hspData && nomeCidade && ufSigla) {
      hspData = await getHsp(nomeCidade, ufSigla);
    }
  } catch (e) {
    console.warn('Erro ao buscar HSP:', e);
  }

  if (!hspData) {
    setErro({ tipo: 1, msg: `Índice solarimétrico não encontrado para ${nomeCidade}/${ufSigla}. Verifique a cidade de instalação.` });
    return;
  }

  const hspMedia = hspData.hspMedia;

  // ── Buscar Performance Ratio (PR) dos índices ────────────────────
  let estadoPR;
  try {
    estadoPR = resolverPR({ proposta });
    if (estadoPR.pr === null) estadoPR = resolverPR({ proposta, indices: await getIndices() });
  } catch (e) {
    setErro({ tipo: 1, msg: e.message || 'Não foi possível consultar o PR. Tente novamente.' });
    return;
  }
  const { pr } = estadoPR;
  // Também mantém o PR quando não houver kit: a escolha manual posterior
  // deve usar o mesmo snapshot do dimensionamento que a originou.
  setProposta((prev) => ({ ...prev, pr, hsp: hspData, fatorOrientacao }));

  // ── Calcular Potência CC necessária ──────────────────────────────
  // PotCC = ConsumoAbatido / (HSP_médio × 30 × PR × FatorOrientação)
  const potCCNecessaria = proposta.mediaconsumoabatida / (hspMedia * 30 * pr * fatorOrientacao);

  // ── Filtrar kits por fornecedor e módulo ─────────────────────────
  const kitsFornMod = kits
    .filter((kit) => kit.fornecedor?._id === fornecedorId)
    .filter((kit) => kit.modulo._id === moduloId);

  if (kitsFornMod.length === 0) {
    const adequacao = classificarAdequacaoKit({
      potenciaNecessaria: potCCNecessaria,
      semKit: true,
    });
    setAdequacaoKit?.(adequacao);
    setErro({ tipo: 1, msg: `Nenhum kit encontrado para o fornecedor e módulo selecionados.` });
    setProposta((prev) => ({
      ...prev,
      kit: null,
      valorkit: 0,
      valor: 0,
      potCCNecessaria: parseFloat(potCCNecessaria.toFixed(4)),
    }));
    return;
  }

  // ── Selecionar kit imediatamente superior à PotCC necessária ─────
  const normalizarPotencia = (valor) => {
    if (typeof valor === 'number') return valor;
    if (typeof valor === 'string') {
      const numero = Number(valor.replace(',', '.').trim());
      return Number.isFinite(numero) ? numero : NaN;
    }
    return NaN;
  };

  const kitsOrdenados = [...kitsFornMod]
    .map((kit) => ({ ...kit, _potenciaNormalizada: normalizarPotencia(kit.potenciacc) }))
    .filter((kit) => Number.isFinite(kit._potenciaNormalizada))
    .sort((a, b) => a._potenciaNormalizada - b._potenciaNormalizada);

  if (kitsOrdenados.length === 0) {
    setAdequacaoKit?.(classificarAdequacaoKit({
      potenciaNecessaria: potCCNecessaria,
      semKit: true,
    }));
    setErro({ tipo: 1, msg: 'Os kits encontrados não possuem potência CC válida para o dimensionamento.' });
    return;
  }

  const kitsSuperiores = kitsOrdenados.filter(
    (kit) => kit._potenciaNormalizada >= potCCNecessaria
  );

  let kitFiltrado;
  let alertaInsuficiencia = null;
  let inversorEfetivo = inversorSelecionado || proposta.topologia || null;

  if (kitsSuperiores.length === 0) {
    const maiorKit = kitsOrdenados[kitsOrdenados.length - 1];
    alertaInsuficiencia = {
      tipo: 2,
      msg: `Atenção: a potência CC necessária é de ${potCCNecessaria.toFixed(2)} kWp, porém o maior kit disponível (${maiorKit?.nome || 'sem identificação'} - ${maiorKit?._potenciaNormalizada?.toFixed(2)} kWp) é insuficiente. O TriAxis selecionou o maior kit disponível; revise o dimensionamento ou cadastre um kit de maior potência.`
    };

    kitFiltrado = maiorKit;

    const unicoInversor = maiorKit?.inversor ? [maiorKit.inversor] : [];
    setInversoresDisponiveis(unicoInversor);

    if (unicoInversor.length === 1) {
      inversorEfetivo = unicoInversor[0];
      setInversorSelecionado(unicoInversor[0]);
    }
  } else {
    // Primeiro define a MENOR potência de kit capaz de atender o cálculo.
    // Só depois procura as alternativas de inversor nessa mesma potência.
    // Assim um inversor híbrido de 9,92 kWp não aparece como opção quando
    // existe um kit de 7,44 kWp suficiente para o consumo, por exemplo.
    const menorPotCC = kitsSuperiores[0]._potenciaNormalizada;
    const kitsDaPotencia = kitsSuperiores.filter(
      (kit) => Math.abs(kit._potenciaNormalizada - menorPotCC) < 0.0001
    );

    // Lista única de inversores disponíveis entre os kits dessa potência.
    const mapaInversores = new Map();
    kitsDaPotencia.forEach((kit) => {
      const inversor = kit.inversor;
      if (inversor?._id) {
        mapaInversores.set(inversor._id.toString(), inversor);
      }
    });
    const novosInversores = Array.from(mapaInversores.values());
    setInversoresDisponiveis(novosInversores);

    if (novosInversores.length === 0) {
      setErro({
        tipo: 1,
        msg: `Os kits de ${menorPotCC.toFixed(2)} kWp não possuem inversor cadastrado. Revise o cadastro dos kits.`,
      });
      setProposta((prev) => ({ ...prev, kit: null, valorkit: 0, valor: 0 }));
      return;
    }

    // Se só existe uma alternativa, seleciona automaticamente e mantém o
    // filtro visível (desabilitado) para o usuário saber qual inversor foi usado.
    if (novosInversores.length === 1) {
      inversorEfetivo = novosInversores[0];
      if (inversorSelecionado?._id !== inversorEfetivo?._id) {
        setInversorSelecionado(inversorEfetivo);
      }
      kitFiltrado = kitsDaPotencia.find(
        (kit) => kit.inversor?._id?.toString() === inversorEfetivo?._id?.toString()
      );
    } else {
      // Se havia uma seleção anterior, só a mantém se ainda existir na nova
      // potência calculada. Caso contrário, exige nova escolha.
      const idSelecionado = inversorEfetivo?._id?.toString();
      const selecaoAindaValida = idSelecionado
        ? novosInversores.some((inv) => inv?._id?.toString() === idSelecionado)
        : false;

      if (!selecaoAindaValida) {
        inversorEfetivo = null;
        setInversorSelecionado(null);
        setErro({
          tipo: 1,
          msg: `Encontrados ${novosInversores.length} inversores disponíveis para o kit de ${menorPotCC.toFixed(2)} kWp. Selecione o inversor desejado.`,
        });
        setProposta((prev) => ({
          ...prev,
          topologia: null,
          kit: null,
          valorkit: 0,
          valor: 0,
        }));
        return;
      }

      kitFiltrado = kitsDaPotencia.find(
        (kit) => kit.inversor?._id?.toString() === idSelecionado
      );
    }

    if (!kitFiltrado) {
      setErro({
        tipo: 1,
        msg: `Kit não encontrado para o inversor selecionado na potência de ${menorPotCC.toFixed(2)} kWp.`,
      });
      return;
    }
  }

  const adequacaoKit = classificarAdequacaoKit({
    potenciaNecessaria: potCCNecessaria,
    potenciaKit: kitFiltrado?._potenciaNormalizada ?? kitFiltrado?.potenciacc,
  });
  setAdequacaoKit?.(adequacaoKit);

  // ── Valor do kit ─────────────────────────────────────────────────
  // Suporta precovenda como número (novo) ou array por UF (legado)
  let valorKitFiltrado = typeof kitFiltrado?.precovenda === 'number'
    ? kitFiltrado.precovenda
    : kitFiltrado?.precovenda?.find((item) => item.uf === ufSigla)?.precovenda;
  const valorOld = typeof proposta.kit?.precovenda === 'number'
    ? proposta.kit.precovenda
    : proposta.kit?.precovenda?.find((item) => item.uf === ufSigla)?.precovenda;
  if (valorKitFiltrado !== valorOld && proposta.etapa.id >= 2) {
    window.alert('ATENÇÃO: Houve mudança no valor do kit, revise a proposta');
  }

  // se for orientacao sul/sudeste e rebatimento true, adicionar 55 reais por modulo
  if (proposta.orientacaotelhado?._id === ID_ORIENTACAO_SUL_SUDOESTE_SUDESTE && proposta.rebatimento) {
    valorKitFiltrado += 55 * kitFiltrado.quantidademodulos;
  }

  // ── Calcular valor final com desconto/acréscimo se existirem ─────
  let valorFinalKit = valorKitFiltrado;
  if (proposta.desconto) {
    valorFinalKit = tipoDesconto === '%'
      ? valorKitFiltrado * (1 - proposta.desconto / 100)
      : valorKitFiltrado - proposta.desconto;
  }
  if (proposta.acrescimo && termoAcrescimoAceito) {
    valorFinalKit = tipoAcrescimo === '%'
      ? valorFinalKit * (1 + proposta.acrescimo / 100)
      : valorFinalKit + proposta.acrescimo;
  }
  valorFinalKit = parseFloat(valorFinalKit.toFixed(2));

  // CORRIGIDO (deployment Marcki — campos "alternando" sozinhos, ex.: tipo de
  // disjuntor voltando ao valor anterior): esta função faz chamadas de rede
  // (getHspIbge/getHsp/getIndices) antes de chegar aqui. Se o usuário mudar
  // qualquer outro campo da proposta ENQUANTO essas chamadas ainda estão em
  // andamento, o "proposta" usado abaixo seria uma foto antiga (capturada
  // quando a função começou), e o setProposta sobrescreveria a mudança mais
  // recente do usuário. Trocado para a forma funcional do setState — que
  // sempre parte do estado mais atual do React, nunca de uma foto antiga —
  // e explicitando só os campos que esta função realmente deve alterar.
  setProposta((prev) => ({
    ...prev,
    kit: (() => {
      const { _potenciaNormalizada, ...kitLimpo } = kitFiltrado || {};
      return kitLimpo;
    })(),
    valorkit: valorKitFiltrado,
    valor: valorFinalKit,
    teste: true,
    topologia: inversorEfetivo,
    hsp: hspData,
    pr,
    fatorOrientacao,
    potCCNecessaria: parseFloat(potCCNecessaria.toFixed(4)),
  }));
  if (adequacaoKit?.status === 'subdimensionado') {
    setErro({ tipo: 1, msg: adequacaoKit.mensagem });
  } else if (alertaInsuficiencia) {
    setErro(alertaInsuficiencia);
  } else {
    setErro(estadoPR.aviso ? { tipo: 2, msg: estadoPR.aviso } : false);
  }
}
